import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { FEATURE, PAGE } from "components/common/CONSTANTS";
import { LabelWithControl } from "components/common/LabelControl";
import { DataGrid } from "components/DataGrid/DataGrid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showErrorMessageByCode } from "services/commonServices";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { SupplierSiteIdSearchFeild } from "components/common/SupplierSiteIdSearchFeild";
import { SupplierDestinationConfigColumnDef } from "./supplierDestinationConfigColumnDef";
import {
  searchSupplierDestinationMappingAction,
  searchSupplierDestinationMappingReset,
} from "redux/actions/searchSupplierDestinationMappingAction";
import {
  addSupplierDestinationMappingAction,
  addSupplierDestinationMappingReset,
} from "redux/actions/addSupplierDestinationMappingAction";
import dayjs from "dayjs";
import { encryptString } from "components/common/EncryptDecrypt";
import {
  removeSupplierDestinationMappingAction,
  removeSupplierDestinationMappingReset,
} from "redux/actions/removeSupplierDestinationMappingAction";
import MarqueeComponent from "components/common/D1D2ConfigurationTimeAlert";
import ConfirmDialog from "components/common/ConfirmDialogD1D2";

export const SupplierDestinationConfiguration = () => {
  const dispatch = useDispatch();
  const userType = useSelector((state) => state.user);
  const { locationResponse } = useSelector((state) => state.locations);
  const searchSupplierDestinationMapping = useSelector(
    (state) => state.searchSupplierDestinationMapping
  );
  const addSupplierDestinationMapping = useSelector(
    (state) => state.addSupplierDestinationMapping
  );
  const removeSupplierDestinationMapping = useSelector(
    (state) => state.removeSupplierDestinationMapping
  );
  const LIMIT = 100;
  const [error, setError] = useState();
  const [searchResultData, setSearchResultData] = useState([]);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllDataFetched, setAllDataFetched] = useState(false);
  const [supplierSiteId, setSupplierSiteId] = useState("");
  const [supplierSiteDisplayName, setSupplierSiteDisplayName] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [validDestinationLocations, setValidDestinationLocations] = useState(
    []
  );
  const [isRemoveSuccessDialog, setRemoveSuccessDialog] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [onConfirmAction, setOnConfirmAction] = useState(() => () => { });
  const [confirmMessage, setConfirmMessage] = useState("");

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      sortable: false,
      filter: false,
      resizable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      headerClass: "header-wrap",
      autoSizeStrategy: { type: "fitCellContents" },
    };
  }, []);

  useEffect(() => {
    const validLocations = locationResponse.map(
      (location) => location.locationId
    );
    setValidDestinationLocations(validLocations);
  }, [locationResponse]);

  const callSearchApi = useCallback(
    (offset, sourceId, destinationId) => {
      if (offset === 0) {
        setSearchResultData([]);
      }

      let payload = {
        limit: LIMIT,
        offset: offset,
      };

      if (sourceId && sourceId.length > 0) {
        payload["sourceId"] = sourceId;
      }
      if (destinationId && destinationId.length > 0) {
        payload["destinationId"] = destinationId;
      }
      const feature = FEATURE.D1D2;
      dispatch(searchSupplierDestinationMappingAction(payload, feature));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!supplierSiteId && !destinationLocation) {
      setRemoveSuccessDialog(false);
      callSearchApi(0);
    }
    setError(null);
  }, [callSearchApi, supplierSiteId, destinationLocation]);

  useEffect(() => {
    if (searchSupplierDestinationMapping.isSearchLoading) {
      setShowSpinner(true);
    } else if (searchSupplierDestinationMapping.isSearchSuccess) {
      setShowSpinner(false);
      const data =
        searchSupplierDestinationMapping
          .searchSupplierDestinationMappingResponse.data;
      setSearchResultData([...searchResultData, ...data]);
      dispatch(searchSupplierDestinationMappingReset());
    } else if (searchSupplierDestinationMapping.isSearchFail) {
      dispatch(searchSupplierDestinationMappingReset());
      setShowSpinner(false);
      if (
        searchSupplierDestinationMapping
          .searchSupplierDestinationMappingResponse?.response?.status === 404
      ) {
        if (searchResultData.length <= 0) {
          setApiErrorMessage("No data found");
        } else {
          setAllDataFetched(true);
        }
      } else {
        setApiErrorMessage("");
        const { errContent } = showErrorMessageByCode(
          searchSupplierDestinationMapping.searchSupplierDestinationMappingResponse
        );
        setApiErrorMessage(errContent);
      }
    }
  }, [searchSupplierDestinationMapping, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (addSupplierDestinationMapping.isAddSupplierDestinationMappingLoading) {
      setShowSpinner(true);
    } else if (
      addSupplierDestinationMapping.isAddSupplierDestinationMappingSuccess
    ) {
      setShowSpinner(false);
      setSuccessDialogMessage(
        `Supplier & Destination mapping added successfully`
      );
      setShowSuccessDialog(true);
      setRemoveSuccessDialog(false);
      dispatch(addSupplierDestinationMappingReset());
    } else if (
      addSupplierDestinationMapping.isAddSupplierDestinationMappingFail
    ) {
      dispatch(addSupplierDestinationMappingReset());
      setShowSpinner(false);
      const errorRes =
        addSupplierDestinationMapping.addSupplierDestinationMappingResponse
          ?.response;
      if (errorRes?.status >= 400 && errorRes?.status <= 499) {
        dispatch(
          showNotificationDialog("Error", errorRes?.data?.errorMessage, "Ok")
        );
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(
          addSupplierDestinationMapping.addSupplierDestinationMappingResponse
        );
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
      }
    }
  }, [addSupplierDestinationMapping, callSearchApi, dispatch]);

  useEffect(() => {
    if (
      removeSupplierDestinationMapping.isRemoveSupplierDestinationMappingLoading
    ) {
      setShowSpinner(true);
    } else if (
      removeSupplierDestinationMapping.isRemoveSupplierDestinationMappingSuccess
    ) {
      setShowSpinner(false);
      setSuccessDialogMessage(
        `${removeSupplierDestinationMapping.removeSupplierDestinationMappingResponse.data.totalDeletedItem} Mapping(s) removed successfully`
      );
      setShowSuccessDialog(true);
      setRemoveSuccessDialog(true);
      dispatch(removeSupplierDestinationMappingReset());
    } else if (
      removeSupplierDestinationMapping.isRemoveSupplierDestinationMappingFail
    ) {
      dispatch(removeSupplierDestinationMappingReset());
      setShowSpinner(false);
      const { errTitle, errContent } = showErrorMessageByCode(
        removeSupplierDestinationMapping.removeSupplierDestinationMappingResponse
      );
      dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
    }
  }, [removeSupplierDestinationMapping, callSearchApi, dispatch]);

  const handleDestinationValueChange = (value) => {
    setDestinationLocation(value ? value : "");
  };

  const isValidDestinationLocation = () => {
    const errors = {};
    if (
      destinationLocation &&
      !validDestinationLocations.includes(parseInt(destinationLocation))
    ) {
      errors.destinationLocationtext = "Invalid destination location";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return false;
    } else {
      return true;
    }
  };

  const handleAddClick = () => {
    setConfirmMessage("Are you sure you want to add this mapping?");

    setOnConfirmAction(() => handleAdd);

    setConfirmOpen(true);
  };

  const handleRemoveClick = () => {
    setConfirmMessage("Are you sure you want to remove these mapping(s)?");

    setOnConfirmAction(() => handleRemoveButton);

    setConfirmOpen(true);
  };

  const handleAdd = async () => {
    if (isValidDestinationLocation()) {
      const encryptedEmailId = await encryptString(
        userType.loginResponse?.email
      );
      const payload = {
        sourceId: supplierSiteId,
        destinationId: destinationLocation,
        createdAt: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
        userId: encryptedEmailId,
        feature: FEATURE.D1D2,
      };
      dispatch(addSupplierDestinationMappingAction(payload));
    }
  };

  const handleSearch = async () => {
    if (!destinationLocation || isValidDestinationLocation()) {
      callSearchApi(0, supplierSiteId, destinationLocation);
    }
  };

  const handleRemoveButton = async () => {
    if (selectedRows.length > 100) {
      dispatch(
        showNotificationDialog(
          "Error",
          "Maximum 100 items can be removed at a time",
          "Ok"
        )
      );
    } else {
      const idList = selectedRows.map((item) => {
        return item.sourceDestinationMappingId;
      });
      const payload = {
        "sourceDestinationMappingId[in]": idList.join(","),
      };
      dispatch(removeSupplierDestinationMappingAction(payload));
    }
  };

  const handleSuccessDialogOkPress = () => {
    setSupplierSiteId("");
    setSupplierSiteDisplayName("");
    setDestinationLocation("");
    setSelectedRows([]);
    setSuccessDialogMessage("");
    setSearchResultData([]);
    setShowSuccessDialog(false);
    if (isRemoveSuccessDialog) {
      callSearchApi(0);
      setRemoveSuccessDialog(false);
    }
  };

  const getRowStyle = (params) => {
    if (params.data.status === "inactive") {
      return { backgroundColor: "#f0f0f0", color: "#999" };
    }
    return null;
  };

  const onCellValueChanged = (event) => {
    if (event.colDef.field === "selected") {
      const row = event.data;
      if (row.selected) {
        setSelectedRows([...selectedRows, row]);
      } else {
        const filteredList = selectedRows.filter(
          (item) => item.item !== row.item
        );
        setSelectedRows(filteredList);
      }
    }
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedRows(selected);
  };

  const onBodyScroll = (params) => {
    var bottomPx = params.api.getVerticalPixelRange().bottom;
    var gridHeight =
      params.api.getDisplayedRowCount() *
      params.api.getSizesForCurrentTheme().rowHeight;
    if (!isAllDataFetched && bottomPx >= gridHeight - 100) {
      if (searchResultData.length % LIMIT === 0) {
        callSearchApi(
          searchResultData.length,
          supplierSiteId ? supplierSiteId : null,
          destinationLocation ? destinationLocation : null
        );
      }
    }
  };

  return (
    <>
      <div className="mainPageStyling">
        {showSpinner && (
          <Backdrop
            sx={{
              color: "#fff",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />

            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading...
            </Typography>
          </Backdrop>
        )}
        <ConfirmDialog
          open={confirmOpen}
          title="Confirm"
          content={confirmMessage}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            onConfirmAction();

            setConfirmOpen(false);
          }}
        />
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
            {PAGE.SUPPLIER_DESTINATION_CONFIGURATION.label}
          </Typography>
          <MarqueeComponent />
        </div>
        <div className="labelControl">
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              marginBottom: "10px",
            }}
          >
            <Grid container spacing={2} sx={{ "& > *": { flex: 1 } }}>
              <Grid item xs={12} sm={4} md={6}>
                <Grid container spacing={2} alignItems="flexStart">
                  <Grid item xs={12} sm={12} md={6}>
                    <SupplierSiteIdSearchFeild
                      isRequired={false}
                      existingSupplierSiteId={supplierSiteId}
                      existingSupplierSiteDisplayName={supplierSiteDisplayName}
                      onValueChange={(event, newValue) => {
                        setSupplierSiteId(newValue?.id);
                        setSupplierSiteDisplayName(
                          newValue?.searchOptionToDisplay
                        );
                      }}
                      error={!supplierSiteId ? error?.supplierSiteIdText : ""}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6}>
                    <LabelWithControl label="Destination Location">
                      <TextField
                        id="destinationLocation"
                        variant="outlined"
                        size="small"
                        placeholder="Destination Location"
                        helperText={error?.destinationLocationtext || ""}
                        error={error?.destinationLocationtext}
                        value={destinationLocation}
                        onChange={(e) =>
                          handleDestinationValueChange(
                            e.target.value.replace(/[^0-9 ]/g, "")
                          )
                        }
                        inputProps={{ maxLength: 32 }}
                      />
                    </LabelWithControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={4} md={1}>
                <LabelWithControl
                  labelVisible="hidden"
                  label="Order ID"
                  labelId="orderId"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      width: "-webkit-fill-available",
                      height: "25px",
                      fontSize: "12px",
                    }}
                    onClick={handleAddClick}
                    disabled={!supplierSiteId || !destinationLocation}
                    fullWidth
                  >
                    Add
                  </Button>
                </LabelWithControl>
              </Grid>

              <Grid item xs={12} sm={4} md={1}>
                <LabelWithControl
                  labelVisible="hidden"
                  label="Order ID"
                  labelId="orderId"
                >
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      width: "-webkit-fill-available",
                      height: "25px",
                      fontSize: "12px",
                    }}
                    onClick={handleSearch}
                    disabled={!supplierSiteId && !destinationLocation}
                    fullWidth
                  >
                    Search
                  </Button>
                </LabelWithControl>
              </Grid>
            </Grid>
          </Grid>
          {searchResultData.length > 0 ? (
            <div style={{ width: "100%", height: "calc( 100vh - 340px)" }}>
              <DataGrid
                key={SupplierDestinationConfigColumnDef}
                columnDefs={SupplierDestinationConfigColumnDef}
                rowData={searchResultData}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                getRowStyle={getRowStyle}
                onCellValueChanged={onCellValueChanged}
                onSelectionChanged={onSelectionChanged}
                onBodyScroll={onBodyScroll}
              />
            </div>
          ) : (
            <div
              style={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "red",
                marginBottom: "10px",
              }}
            >
              {apiErrorMessage}
            </div>
          )}
        </div>
      </div>

      {searchResultData.length > 0 && (
        <>
          <Grid container spacing={2}>
            {/* Right side buttons */}

            <Grid item xs={6} container justifyContent="flex-start" spacing={2}>
              <Grid item>
                <div className="parentPoButtons">
                  <Button
                    className="commonPOButtons"
                    variant="contained"
                    color="primary"
                    disabled={selectedRows.length <= 0}
                    onClick={handleRemoveClick}
                  >
                    REMOVE
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      <Dialog open={showSuccessDialog}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{successDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogOkPress} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
